import { Params } from '@angular/router';
import { params } from '@app/configurations';
import { ActiveAdPlatform, AdPlatform } from '@app/core/models/common.models';
import { arrayUnique, assertArray } from '@app/core/utilities/array.utilities';
import { assertObject, deepClone, groupBy } from '@app/core/utilities/object.utilities';
import { extractRulesRefiners } from '@app/core/utilities/refiners.utilities';
import { snakeToCamelCase } from '@app/core/utilities/string.utilities';
import { getRawQueryParams } from '@app/core/utilities/url.utilities';
import {
  CriterionAdditionCampaignType,
  CriterionAdditionCriterionSubtype,
  CriterionAdditionCriterionType,
  CriterionAdditionProposalType,
} from '@app/criterion-addition/shared/models/criterion-addition.models';
import { DestinationType } from '@app/criterion-addition/shared/models/destination.models';
import {
  CampaignAdGroupTabRule,
  CampaignTypeGroupedRule,
  Rule,
  RuleAction,
  RuleFilter,
  RuleFilterOperand,
  RuleFilterOperation,
  RuleInstance,
  RuleLevel,
  RuleLocation,
  RuleSet,
  RuleSetsWithCampaignType,
  RuleSetWithRuleType,
  RulesTab,
  RuleType,
  SupportedCriteria,
} from '@app/criterion-addition/shared/models/rule.models';
import { Suggestion } from '@app/criterion-addition/shared/models/suggestions.models';
import {
  getDestinationAdgroupId,
  isValidDestination,
} from '@app/criterion-addition/shared/modules/destination-match-type/utilities/destination-match-type.utitilies';

export const ruleLevelRank = [
  RuleLevel.Default,
  RuleLevel.Platform,
  RuleLevel.Account,
  RuleLevel.Campaign,
  RuleLevel.AdGroup,
];

const filterOperationByOperand = {
  [RuleFilterOperand.Keyword]: [
    RuleFilterOperation.StartsWith,
    RuleFilterOperation.NotStartsWith,
    RuleFilterOperation.EndsWith,
    RuleFilterOperation.NotEndsWith,
    RuleFilterOperation.Contains,
    RuleFilterOperation.NotContains,
    RuleFilterOperation.ContainsWord,
    RuleFilterOperation.NotContainsWord,
    RuleFilterOperation.Equals,
    RuleFilterOperation.NotEquals,
  ],
  [RuleFilterOperand.KeywordLength]: [
    RuleFilterOperation.Equals,
    RuleFilterOperation.NotEquals,
    RuleFilterOperation.GreaterThan,
    RuleFilterOperation.GreaterThanOrEqual,
    RuleFilterOperation.LessThan,
    RuleFilterOperation.LessThanOrEqual,
  ],
  [RuleFilterOperand.KeywordWordCount]: [
    RuleFilterOperation.Equals,
    RuleFilterOperation.NotEquals,
    RuleFilterOperation.GreaterThan,
    RuleFilterOperation.GreaterThanOrEqual,
    RuleFilterOperation.LessThan,
    RuleFilterOperation.LessThanOrEqual,
  ],
  [RuleFilterOperand.Domain]: [
    RuleFilterOperation.StartsWith,
    RuleFilterOperation.NotStartsWith,
    RuleFilterOperation.EndsWith,
    RuleFilterOperation.NotEndsWith,
    RuleFilterOperation.Contains,
    RuleFilterOperation.NotContains,
    RuleFilterOperation.Equals,
    RuleFilterOperation.NotEquals,
  ],
};

export function duplicateRule(source: RuleSet): RuleSet {
  const rule = deepClone(source);
  rule.inherited = false;
  rule.rules?.forEach((rule, i) => (rule.ruleId = -1 - i));

  return rule;
}

export function newRuleFromSource(source: RuleSet): RuleSet {
  const rule = duplicateRule(source);
  const instance = getNewRuleInstance();

  rule.rules = [instance];

  return rule;
}

export function ruleLevelToId(level: RuleLevel): number {
  return ruleLevelRank.indexOf(level);
}

export function getRuleTypeTranslationLabel(ruleType: RuleType): string {
  return `ca.rules.rule-type.${ruleType.proposalType}.${[
    ...assertArray(ruleType.criterionSubtype),
    ruleType.criterionType,
  ].join('-')}`;
}

export function getRuleTypeFromRule(rule: RuleSet, ruleTypes: RuleType[]): RuleType | undefined {
  return ruleTypes.find((ruleType) => ruleType.ruleTypeId === rule?.ruleTypeId);
}

export function isNoFilterRuleMode(rule: RuleSet): boolean {
  return assertArray(rule.rules).length <= 1;
}

export function canRuleFilter(rule: RuleSet, ruleTypes: RuleType[]): boolean {
  return canRuleTypeFilter(getRuleTypeFromRule(rule, ruleTypes));
}

export function canRuleTypeFilter(ruleType: RuleType | undefined): boolean {
  if (!ruleType) {
    return false;
  }

  if (ruleType.criterionType === CriterionAdditionCriterionType.Keyword) {
    return true;
  }

  return (
    ruleType.criterionType === CriterionAdditionCriterionType.Placement &&
    [CriterionAdditionCriterionSubtype.Url, CriterionAdditionCriterionSubtype.Domain].includes(
      ruleType.criterionSubtype,
    )
  );
}

export function hasRuleFilters(rules: RuleInstance[]): boolean {
  return !!rules?.some((rule) => !!rule.filters?.length);
}

export function isRuleSetComplete(ruleSet: RuleSet, isRadioCriterion: boolean): boolean {
  if (!ruleSet.rules?.length) {
    return false;
  }

  const isNoFilterMode = isNoFilterRuleMode(ruleSet);

  return ruleSet.rules.every((rule) => {
    if (rule.action === RuleAction.Add) {
      if (
        !rule.destinations?.length ||
        !rule.destinations.every((destination) => isValidDestination(destination, isRadioCriterion))
      ) {
        return false;
      }
    }

    return (
      (!rule.filters?.length && isNoFilterMode) ||
      rule.catchAll ||
      rule.filters.every((filter) => isFilterValid(filter))
    );
  });
}

export function getNewRuleInstance(ruleId: number = -1, withFilters?: boolean): RuleInstance {
  return { ruleId, action: RuleAction.Review, ...(withFilters ? { filters: [{}] } : {}) } as RuleInstance;
}

export function getRuleTypeFilterOperands(ruleType: RuleType, filter?: RuleFilter): RuleFilterOperand[] {
  if (!canRuleTypeFilter(ruleType)) {
    return [];
  }

  if (ruleType.criterionType === CriterionAdditionCriterionType.Keyword) {
    const operands = [RuleFilterOperand.Keyword, RuleFilterOperand.KeywordLength, RuleFilterOperand.KeywordWordCount];

    return filter?.operation
      ? operands.filter((operand) => filterOperationByOperand[operand].includes(filter.operation))
      : operands;
  }

  if (ruleType.criterionType === CriterionAdditionCriterionType.Placement) {
    return [RuleFilterOperand.Domain];
  }
}

export function getRuleTypeFilterOperations(ruleType: RuleType, filter?: RuleFilter): RuleFilterOperation[] {
  if (!canRuleTypeFilter(ruleType)) {
    return [];
  }

  if (filter?.operand) {
    return filterOperationByOperand[filter.operand];
  }

  if (ruleType.criterionType === CriterionAdditionCriterionType.Keyword) {
    return arrayUnique([
      ...filterOperationByOperand[RuleFilterOperand.Keyword],
      ...filterOperationByOperand[RuleFilterOperand.KeywordLength],
      ...filterOperationByOperand[RuleFilterOperand.KeywordWordCount],
    ]);
  }

  if (ruleType.criterionType === CriterionAdditionCriterionType.Placement) {
    return [...filterOperationByOperand[RuleFilterOperand.Domain]];
  }
}

export function isFilterValid(filter: RuleFilter): boolean {
  if (!filter) {
    return false;
  }

  if (!filterOperationByOperand[filter.operand]?.includes(filter.operation)) {
    return false;
  }

  return !!filter.values?.length;
}

export function isFilterEmpty(filter: RuleFilter): boolean {
  return !filter || (!filter.values?.length && !filter.operation && !filter.operand);
}

export function isDigitFilter(filter: RuleFilter): boolean {
  return [RuleFilterOperand.KeywordWordCount, RuleFilterOperand.KeywordLength].includes(filter?.operand);
}

export function suggestionToRule(suggestion: Suggestion, action: RuleAction): RuleSet {
  return {
    ruleTypeId: suggestion.ruleTypeId,
    definedAt: {
      ...suggestion.source,
      level: RuleLevel.AdGroup,
      platform: suggestion.source.accountPlatform,
    },
    inherited: false,
    rules: [
      {
        ...suggestion,
        action,
        catchAll: false,
        filters: [],
      } as unknown as RuleInstance,
    ],
  };
}

export function ruleToSaveObject(rule: RuleSet, isRadioCriterion: boolean): any {
  const { definedAt, ruleTypeId } = rule;
  const level = definedAt.level;
  const simpleMode = rule.rules.length === 1;

  const rules = rule.rules
    .map((ruleInstance) => {
      const filters = assertArray(ruleInstance.filters).filter(isFilterValid);
      const destinations = assertArray(ruleInstance.destinations)
        .filter((destination) => isValidDestination(destination, isRadioCriterion))
        .map((destination) => {
          const adspertAdgroupId = getDestinationAdgroupId(destination);

          return {
            destinationType: destination.destinationType,
            ...assertObject(
              adspertAdgroupId && destination.destinationType === DestinationType.SelectedAdgroup
                ? { adspertAdgroupId }
                : undefined,
            ),
            ...assertObject(destination.matchType ? { matchType: destination.matchType } : undefined),
            negative: destination.negative,
          };
        });

      return {
        ...assertObject(ruleInstance.ruleId >= 0 ? { ruleId: ruleInstance.ruleId.toString() } : undefined),
        action: ruleInstance.action,
        ...assertObject(filters.length ? { filters } : undefined),
        catchAll: !!ruleInstance.catchAll,
        ...assertObject(ruleInstance.action !== RuleAction.Reject ? { destinations } : undefined),
      };
    })
    .filter((ruleInstance) => simpleMode || ruleInstance.catchAll || ruleInstance.filters.length);

  return {
    ruleTypeId,
    level,
    rules,
    ...getLevelParams(level, definedAt),
  };
}

export function ruleToDeleteObject(rule: RuleSet): any {
  const { definedAt, ruleTypeId } = rule;
  const level = definedAt.level;
  return {
    ruleTypeId,
    level,
    ...getLevelParams(level, definedAt),
  };
}

function getLevelParams(level: RuleLevel, definedAt: RuleLocation) {
  return {
    ...(level === RuleLevel.Account ? { adspertAccountId: definedAt.adspertAccountId } : {}),
    ...(level === RuleLevel.Campaign ? { adspertCampaignId: definedAt.adspertCampaignId } : {}),
    ...(level === RuleLevel.AdGroup ? { adspertAdgroupId: definedAt.adspertAdgroupId } : {}),
  };
}

export function getLevelColumnName(level: RuleLevel): string | null {
  switch (level) {
    case RuleLevel.Account:
      return 'adspertAccountId';

    case RuleLevel.Campaign:
      return 'adspertCampaignId';

    case RuleLevel.AdGroup:
      return 'adspertAdgroupId';
  }
  return null;
}

export function Types2SupportedCriteria(
  data: RuleType[],
  supportedPlatforms: AdPlatform[],
): SupportedCriteria[] | null {
  if (!data) return null;

  const groupByPlatform = <{ key: AdPlatform; data: RuleType[] }[]>groupBy(data, 'platform', true);
  return groupByPlatform.map(({ key, data }) => {
    return {
      platform: key,
      isSupported: supportedPlatforms.includes(key),
      data: (<any>groupBy(data, 'criterionType', true)).map(({ key, data }) => ({
        key,
        data: [...new Set(data.map((d) => d.campaignType))],
      })),
    } as SupportedCriteria;
  });
}

export function Rules2CampaignTypeTabRules(
  rules: Rule[],
  ruleTypes: { key: CriterionAdditionCampaignType; data: number[] }[],
): CampaignTypeGroupedRule[] {
  const groupedRules = rules.map(({ rulesets, location }) => {
    const ruleSetsWithCampaignType = rulesets
      .map((ruleset) => ({
        ...ruleset,
        campaignType: ruleTypes.find(({ data }) => data.includes(ruleset.ruleTypeId))?.key,
      }))
      .filter((d) => !!d.campaignType);

    const ruleSetsGroupedByCampaignType = groupBy(ruleSetsWithCampaignType, 'campaignType', true).map(
      ({ key, data }) => ({
        type: key,
        rulesets: data.sort((a, b) => (a.inherited ? 1 : b.inherited ? -1 : 0)),
        userDefinedRules: data.filter(({ inherited }) => !inherited).length,
      }),
    );
    return {
      location,
      campaignTypeRulesets: ruleSetsGroupedByCampaignType,
      hasUserDefinedRules: !!ruleSetsGroupedByCampaignType.filter(({ userDefinedRules }) => userDefinedRules > 0)
        .length,
    };
  });

  return <any>groupedRules;
}

export function Rules2CampaignAdGroupTabRules(
  rules: Rule[],
  ruleTypes: { key: CriterionAdditionCampaignType; data: number[] }[],
): CampaignAdGroupTabRule[] {
  const groupedRules = rules.map((rule) => {
    return {
      ...rule,
      userDefinedRules: rule.rulesets.filter(({ inherited }) => !inherited).length,
      rulesets: rule.rulesets
        .map((ruleset: RuleSetWithRuleType) => ({
          ...ruleset,
          campaignType: ruleTypes.find((d) => d.data.includes(ruleset.ruleTypeId)).key,
        }))
        .sort((a, b) => (a.inherited ? 1 : b.inherited ? -1 : 0)),
    };
  });
  return <any>groupedRules;
}
const accountParams = [params.ADSPERT_ID, params.GLOBAL_FILTER_ACCOUNT_QUERY];

const campaignsParams = [params.GLOBAL_FILTER_CAMPAIGN_ID, params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID];

const adGroupParams = [params.GLOBAL_FILTER_AD_GROUPS_ID, params.GLOBAL_FILTER_AD_GROUPS_QUERY];

function getCampaignParams(): string[] {
  const state = getRawQueryParams();
  const tmp = [...campaignsParams];

  if (state[params.GLOBAL_FILTER_CAMPAIGN_QUERY] !== '') tmp.push(params.GLOBAL_FILTER_CAMPAIGN_QUERY);
  if (state[params.GLOBAL_FILTER_PERFORMANCE_GROUP_QUERY] !== '')
    tmp.push(params.GLOBAL_FILTER_PERFORMANCE_GROUP_QUERY);

  return tmp;
}

const hasValidParam = (state, keys) => keys.filter((key) => state[key] == null).length !== keys.length;

export const hasGlobalFilterSelections = (state) =>
  hasValidParam(state, [...adGroupParams, ...getCampaignParams(), ...accountParams]);

export function GetActiveTab() {
  const state = getRawQueryParams();

  const tabValue = state[params.RULES_TAB] == null ? 0 : +state[params.RULES_TAB];

  switch (true) {
    case hasValidParam(state, adGroupParams):
      return tabValue > RulesTab.Adgroup ? tabValue : RulesTab.Adgroup;
    case hasValidParam(state, getCampaignParams()):
      return tabValue > RulesTab.Campaign ? tabValue : RulesTab.Campaign;
    case hasValidParam(state, accountParams):
      return tabValue > RulesTab.Account ? tabValue : RulesTab.Account;
    case hasGlobalFilterSelections(state):
      return RulesTab.Platform;
  }

  return tabValue;
}

export function NextSortOrder(sort) {
  if (sort == undefined) return 1;
  else if (!isNaN(sort) && sort > 0) return Math.abs(sort) * -1;
  else if (!isNaN(sort) && sort < 0) return null;
}

export function GetRuleFilters(state = getRawQueryParams()) {
  const rulesQuery = state[params.GLOBAL_FILTER_RULES_QUERY];
  if (!rulesQuery) return {};
  return extractRulesRefiners(rulesQuery)
    .filter((d) => d[0].startsWith('filter'))
    .reduce((acc, [key, value]) => {
      acc[snakeToCamelCase(key.replace('filter_', ''))] = value;
      return acc;
    }, {});
}

export function hasRuleTypeInCampaignTypeRulesets(
  campaignTypeRulesets: RuleSetsWithCampaignType[],
  ruleTypeID: number,
  needIndex = false,
) {
  const index = campaignTypeRulesets.findIndex(({ rulesets }) =>
    rulesets.find(({ ruleTypeId }) => ruleTypeId === ruleTypeID),
  );

  if (needIndex) return index;
  return index !== -1;
}

export function hasRuleTypeInRulesets(rulesets: RuleSetWithRuleType[], ruleTypeID: number, needIndex = false) {
  const index = rulesets.findIndex(({ ruleTypeId }) => ruleTypeId === ruleTypeID);
  if (needIndex) return index;
  return index !== -1;
}

// shouldn't this just be scroll to element? why is it called accordion? and what is it doing in rule utilities :D
export function scrollToAccordion(query: string) {
  const ele: Element = document.querySelector(query);
  if (!ele) return;
  window.scroll({
    top: ele.getBoundingClientRect().top + window.scrollY - 150,
    behavior: 'smooth',
  });
}
export const DestinationFiltersQueryParams = [
  params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_QUERY,
  params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_ID,
  params.GLOBAL_FILTER_DEST_CAMPAIGN_ID,
  params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY,
  params.GLOBAL_FILTER_DEST_AD_GROUPS_ID,
  params.GLOBAL_FILTER_DEST_AD_GROUPS_QUERY,
];
export function stateHasDestinationFilters(state: Params): boolean {
  return !!Object.keys(state).find((key) => DestinationFiltersQueryParams.includes(key));
}

const CriterionTypeNumbering: [
  CriterionAdditionProposalType,
  CriterionAdditionCriterionType | CriterionAdditionCriterionSubtype,
][] = [
  [CriterionAdditionProposalType.Addition, CriterionAdditionCriterionType.Keyword],
  [CriterionAdditionProposalType.Exclusion, CriterionAdditionCriterionType.Keyword],
  [CriterionAdditionProposalType.Addition, CriterionAdditionCriterionSubtype.Asin],
  [CriterionAdditionProposalType.Exclusion, CriterionAdditionCriterionSubtype.Asin],
  [CriterionAdditionProposalType.Addition, CriterionAdditionCriterionSubtype.Domain],
  [CriterionAdditionProposalType.Exclusion, CriterionAdditionCriterionSubtype.Domain],
  [CriterionAdditionProposalType.Addition, CriterionAdditionCriterionSubtype.Url],
  [CriterionAdditionProposalType.Exclusion, CriterionAdditionCriterionSubtype.Url],
  [CriterionAdditionProposalType.Addition, CriterionAdditionCriterionType.MobileApplication],
  [CriterionAdditionProposalType.Exclusion, CriterionAdditionCriterionType.MobileApplication],
  [CriterionAdditionProposalType.Addition, CriterionAdditionCriterionType.YoutubeVideo],
  [CriterionAdditionProposalType.Exclusion, CriterionAdditionCriterionType.YoutubeVideo],
  [CriterionAdditionProposalType.Addition, CriterionAdditionCriterionType.YoutubeChannel],
  [CriterionAdditionProposalType.Exclusion, CriterionAdditionCriterionType.YoutubeChannel],
];

export const PlatformCampaignTypeNumbering: Record<ActiveAdPlatform, CriterionAdditionCampaignType[]> = {
  AMAZON: [
    'AMAZON-SPONSORED-PRODUCTS-AUTO',
    'AMAZON-SPONSORED-PRODUCTS-MANUAL',
    'AMAZON-SPONSORED-BRANDS',
    'AMAZON-SPONSORED-DISPLAY',
  ],
  EBAY: ['EBAY-PROMOTED-LISTINGS-ADVANCED'],
  GOOGLE: ['GOOGLE-SEARCH', 'GOOGLE-SHOPPING', 'GOOGLE-DISPLAY'],
  MICROSOFT: ['MICROSOFT-SEARCH', 'MICROSOFT-SHOPPING'],
  WALMART: ['WALMART-SPONSORED-BRANDS', 'WALMART-SPONSORED-PRODUCTS', 'WALMART-SPONSORED-VIDEO'],
};

export function getRuleTypeNumber(ruleType: RuleType): string {
  const major = Object.keys(PlatformCampaignTypeNumbering).indexOf(ruleType.platform) + 1;
  const minor = PlatformCampaignTypeNumbering[ruleType.platform]?.indexOf(ruleType.campaignType) + 1 || 0;
  const patch =
    CriterionTypeNumbering.findIndex(
      ([proposalType, criterionType]) =>
        proposalType === ruleType.proposalType &&
        (criterionType === ruleType.criterionSubtype || criterionType === ruleType.criterionType),
    ) + 1;

  return `${major}.${minor}.${patch}`;
}

export function getRuleSetId(location: RuleLocation, ruleSet: RuleSet): string {
  return `${location.adspertAdgroupId || location.adspertCampaignId || location.adspertAccountId}-${
    ruleSet.ruleTypeId
  }`;
}
